/** Seeks `video` to `time` and resolves once the frame is ready — races the
 * 'seeked' event against a timeout since it may never fire when the requested
 * time equals the video's current position (no-op seeks). */
const seekTo = (video: HTMLVideoElement, time: number): Promise<void> =>
  new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      video.removeEventListener('seeked', finish)
      clearTimeout(timer)
      resolve()
    }
    video.addEventListener('seeked', finish)
    const timer = window.setTimeout(finish, 1500)
    video.currentTime = time
  })

/**
 * Generates `count` evenly-spaced JPEG thumbnail data URLs across the video's
 * full duration by sequentially seeking and capturing a frame each time —
 * the filmstrip background for VideoTrimFilmstrip.tsx. Restores the video's
 * original playback position afterward so callers can keep using the same
 * element for live scrub-preview once this resolves.
 */
export const generateFilmstripThumbnails = async (
  video: HTMLVideoElement,
  count: number,
  thumbWidth = 80,
): Promise<string[]> => {
  const duration = video.duration
  if (!Number.isFinite(duration) || duration <= 0 || count <= 0) return []

  const aspect = (video.videoWidth || 16) / (video.videoHeight || 9)
  const thumbHeight = Math.max(1, Math.round(thumbWidth / aspect))
  const canvas = document.createElement('canvas')
  canvas.width = thumbWidth
  canvas.height = thumbHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return []

  const originalTime = video.currentTime
  const originalMuted = video.muted
  video.muted = true

  const thumbnails: string[] = []
  for (let i = 0; i < count; i++) {
    const t = (duration * i) / Math.max(1, count - 1)
    await seekTo(video, t)
    ctx.drawImage(video, 0, 0, thumbWidth, thumbHeight)
    thumbnails.push(canvas.toDataURL('image/jpeg', 0.6))
  }

  await seekTo(video, originalTime)
  video.muted = originalMuted
  return thumbnails
}
